import clsx from "clsx";
import { FormikProps, FormikValues, getIn } from "formik";
import Input from "@druva-apps/orion/lib/input";
import Tooltip, { TooltipProps } from "@druva-apps/orion/lib/tooltip";
import { ChangeEvent, FC, ReactNode, useCallback } from "react";

type RadioOption = {
  label: string;
  value: string;
  infoText?: string;
  "data-testid"?: string;
  renderExtraInfo?: (isSelected: boolean) => ReactNode;
  disabled?: boolean;
  getTooltipProps?: (values: FormikValues) => TooltipProps;
};

type RadioProps = {
  options: RadioOption[];
  name: string;
  validationProps?: FormikProps<FormikValues>;
  isInvalid: boolean;
  disabled?: boolean;
  inputDirection?: "vertical" | "horizontal";
};

const RadioInput: FC<RadioProps> = ({
  options,
  name,
  validationProps,
  isInvalid,
  disabled = false,
  inputDirection = "vertical",
}) => {
  const isHorizontal = inputDirection === "horizontal";

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      validationProps?.setFieldValue(name, e.target.value);
    },
    [name, validationProps],
  );

  const selectedValue = getIn(validationProps?.values, name);

  const containerClass = clsx("oui-d-flex", {
    "oui-flex-row oui-justify-content-start oui-align-items-center":
      isHorizontal,
    "oui-flex-column": !isHorizontal,
  });

  const gapStyle = isHorizontal ? { gap: "1rem" } : { gap: "0.5rem" };

  return (
    <>
      <div className={containerClass} style={gapStyle}>
        {options.map((option) => {
          const tooltipProps = {
            text: "",
            showToolTip: false,
            ...option.getTooltipProps?.(validationProps?.values || {}),
          };

          return (
            <div key={option.value}>
              <Tooltip {...tooltipProps}>
                <div className="oui-d-inline-block">
                  <Input
                    type="radio"
                    label=""
                    value={option.value}
                    name={name}
                    checked={selectedValue === option.value}
                    onChange={handleChange}
                    disabled={disabled || option.disabled}
                    data-testid={option["data-testid"]}
                    id={`${name}-${option.value}`}
                  />
                </div>
              </Tooltip>
              <label
                htmlFor={`${name}-${option.value}`}
                style={{
                  cursor: disabled || option.disabled ? "default" : "pointer",
                }}
              >
                {option.label}
              </label>
              {option.infoText && (
                <div className="oui-text-muted oui-small oui-ml-4">
                  {option.infoText}
                </div>
              )}
              {option.renderExtraInfo?.(selectedValue === option.value)}
            </div>
          );
        })}
      </div>
      <div
        data-testid="orion-input-feedback"
        className="oui-d-block oui-invalid-feedback"
      >
        {isInvalid && getIn(validationProps?.errors, name)}
      </div>
    </>
  );
};

export default RadioInput;
